
import { Duration, Interval, DateTime } from 'luxon';
import React from "react";
import { pixelPadTimeline, zip } from './utils.js';

import * as d3 from 'd3';


// These cutoffs are as a function of viewport size
const _mtd_cutoffs = [    'PT12H', 'PT36H', 'PT108H',  'P10D',  'P45D', 'P3Y', 'P35Y'   ];
const _mtd_values = ['PT30M',  'PT1H',   'PT3H',  'PT12H', 'P1D',  'P1M', 'P1Y', 'P10Y'];
const mtd_cutoffs = _mtd_cutoffs.map(s => Duration.fromISO(s));
const mtd_values = _mtd_values.map(s => Duration.fromISO(s));

const _dintv_cutoffs = [   'P3D',   'P10D',  'P1Y6M',  'P20Y'   ];
const _dintv_values = ['PT1H',  'PT3H',   'P1D',  'P1M',  'P1Y'];
const dintv_cutoffs = _dintv_cutoffs.map(s => Duration.fromISO(s));
const dintv_values = _dintv_values.map(s => Duration.fromISO(s));

// These cutoffs are as a function of mtd (major tick duration) size
const _blf_cutoffs = [    'PT1H', 'PT24H', 'P7D', 'P1Y'   ];
const _blf_values = ['%H%MZ',  '%HZ',   '%d',  '%m',  null];
const blf_cutoffs = _blf_cutoffs.map(s => Duration.fromISO(s));
const blf_values = _blf_values.map(s => s === null ? () => '' : d3.utcFormat(s));

const _tlf_cutoffs = [        'P1D',    'P7D'    ];
const _tlf_values = ['%Y-%m-%d',  '%Y-%m',   '%Y'];
const tlf_cutoffs = _tlf_cutoffs.map(s => Duration.fromISO(s));
const tlf_values = _tlf_values.map(s => s === null ? () => '' : d3.utcFormat(s));
const tlf_checks = [jsdt => jsdt.getUTCHours() == 0 && jsdt.getUTCMinutes() == 0, 
                    jsdt => jsdt.getUTCDate() == 1,
                    jsdt => jsdt.getUTCMonth() == 0 && jsdt.getUTCDate() == 1]

const orient_cutoff = Duration.fromISO('P1Y');

class TimelinePlot extends React.Component {
    constructor(props) {
        super(props);

        this.state = {};

        this.partitioner = this.props['partitioner'];
        this.wheel = this.wheel.bind(this)
    }

    componentDidMount() {
        this.drawPlot();

        // parentNode ... big ol' yikes
        this.root.parentNode.addEventListener('wheel', this.wheel, {'passive': false});
        // ^ surely there's a way to do this through react? (specifically ^--------------^ this part)
    }

    componentWillUnmount() {
        this.root.parentNode.removeEventListener('wheel', this.wheel);
    }

    componentDidUpdate() {
        this.drawPlot();
    }

    wheel(event) {
        // If the event is not actually in the axes bounds, don't bother.
        // Also, another parentNode. Gonna have to figure out a less janky way to do this.
        let rect = this.root.parentNode.getBoundingClientRect();
        let event_x = event.clientX - rect.x;
        let event_y = event.clientY - rect.y;

        if (event_x < this.props['x'] || event_x > this.props['x'] + this.props['width'] ||
            event_y < this.props['y'] || event_y > this.props['y'] + this.props['height']) {
            return;
        }

        // This is why I need the {'passive': false} event handler
        event.preventDefault();
        const t_bnds_raw = this.props['t-bounds'].getIntervals()[0];
        const [earliest_dt, latest_dt] = pixelPadTimeline(this.props['width'], t_bnds_raw, this.props['t-limits'], this.props['t-buffer']);
        const t_bnds_intv = Interval.fromDateTimes(earliest_dt, latest_dt).intersection(t_bnds_raw);

        const t_scale = d3.scaleUtc()
            .domain([t_bnds_intv.start.toJSDate(),
                     t_bnds_intv.end.toJSDate()])
            .range([this.props['x'], this.props['x'] + this.props['width']]);

        const dt_focus = DateTime.fromJSDate(t_scale.invert(event.clientX), {'zone': 'UTC'});

        var new_start, new_end

        if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
            // If the mouse movement is more in the y direction than the x direction, consider it a zoom movement
            const y_sens = 0.005;
            new_start = dt_focus.minus({'milliseconds': dt_focus.diff(t_bnds_intv.start) * (1 + event.deltaY * y_sens)});
            new_end = dt_focus.plus({'milliseconds': t_bnds_intv.end.diff(dt_focus) * (1 + event.deltaY * y_sens)});

            // Enforce maximum zoom
            if (new_end - new_start < Duration.fromISO('PT2H')) {
                new_start = t_bnds_intv.start;
                new_end = t_bnds_intv.end;
            }
        }
        else {
            // If the mouse movement is more in the x direction than the y direction, consider it a pan movement
            var x_sens = Duration.fromISO('PT1M');
            x_sens = x_sens * t_bnds_intv.toDuration() / Duration.fromISO('PT24H');

            new_start = t_bnds_intv.start.plus(x_sens * event.deltaX);
            new_end = t_bnds_intv.end.plus(x_sens * event.deltaX);
        }

        // Enforce minimum zoom
        new_start = new_start < earliest_dt ? earliest_dt : new_start;
        new_end = new_end > latest_dt ? latest_dt : new_end;

        var new_intv = Interval.fromDateTimes(new_start, new_end);

        if (this.props['onwheel'] !== undefined) {
            this.props['onwheel'](new_intv);
        }
    }
 
    drawPlot() {
        const font_name = 'Trebuchet MS';
        const height = this.props['height'];

        const stepwise = (x_val, x_cutoffs, y_values) => {
            let y_val = null;

            x_cutoffs.some((xc, ic) => {
                if (x_val < xc) {
                    y_val = y_values[ic];
                    return true;
                }
            });

            if (y_val === null) {
                y_val = y_values[y_values.length - 1];
            }

            return y_val;
        }

        const t_bnds_raw = this.props['t-bounds'].getIntervals()[0]
        const [earliest_dt, latest_dt] = pixelPadTimeline(this.props['width'], t_bnds_raw, this.props['t-limits'], this.props['t-buffer']);
        const t_bnds_intv = Interval.fromDateTimes(earliest_dt, latest_dt).intersection(t_bnds_raw);
        const intv_dur = t_bnds_intv.toDuration();

        const mtd = stepwise(intv_dur, mtd_cutoffs, mtd_values);
        const data_dintv = stepwise(intv_dur, dintv_cutoffs, dintv_values);
        const bl_format = stepwise(mtd, blf_cutoffs, blf_values);
        const tl_format = stepwise(mtd, tlf_cutoffs, tlf_values);
        const tl_check = stepwise(mtd, tlf_cutoffs, tlf_checks);
        const draw_orient = mtd < orient_cutoff;

        // Convert luxon durations to d3 objects
        const luxon_d3_conversion = {'years': d3.utcYear, 'months': d3.utcMonth, 'days': d3.utcDay, 
            'hours': d3.utcHour, 'minutes': d3.utcMinute};

        var d3_ticks;
        for (let comp in luxon_d3_conversion) {
            if (mtd[comp] > 0) {
                d3_ticks = luxon_d3_conversion[comp].every(mtd[comp]);
                break
            }
        }

        const time_format = jsdt => {
            let str = bl_format(jsdt);

            if (tl_check(jsdt)) {
                if (str != "") {
                    str += " ";
                }
                str += tl_format(jsdt);
            }
            return str;
        }

        const wrap = text => {
            text.each(function() {
                var text = d3.select(this),
                    words = text.text().split(' ').reverse(),
                    lineNumber = 0;

                const lineHeight = 1.1, // ems
                      y = text.attr("y"),
                      dy = parseFloat(text.attr("dy"));
        
                if (words.length > 1) {
                    text.text(null);
        
                    let iword = 0;
                    let word;
                    while (word = words.pop()) {
                        text.append("tspan")
                            .attr("x", 0)
                            .attr("y", y)
                            .attr('font-size', iword > 0 ? '1.5em' : '1em')
                            .attr("dy", lineNumber++ * lineHeight + dy + "em")
                            .text(word);
                        iword++;
                    }
                }
            });
        }

        let tor_data = [], all_tor_counts = [];

        if (this.props['tor_coll'] !== null) {
            // Get the tornado data we're going to plot
            let bar_subintvs = this.props['t-bounds'].getSubintervals(data_dintv);
            let tor_subintvs = bar_subintvs;

            if (this.props['interval'].isYearly()) {
                tor_subintvs = tor_subintvs.map(sintv => this.props['interval'].intersection(sintv, true));
                [tor_subintvs, bar_subintvs] = [...zip(...[...zip(tor_subintvs, bar_subintvs)].filter(([ts, bs]) => ts !== null))];
            }

            let tor_counts = this.props['tor_coll'].getNTornadoes(tor_subintvs, this.partitioner);

            tor_counts = tor_counts.map(cnt => {
                let p_keys = Object.keys(cnt);
                let p_counts = Object.values(cnt);

                const cumulativeSum = (sum => value => sum += value)(0);
                var cnt_cumulative = p_counts.map(cumulativeSum).slice(0, p_counts.length - 1);
                cnt_cumulative.unshift(0);

                return [...zip(p_keys, p_counts, cnt_cumulative)].map(el => ({'pk': el[0], 'ntor': el[1], 'ntor_base': el[2]}))
                    .filter(el => el['ntor'] > 0);
            });
            tor_data = bar_subintvs.map((e, i) => {
                return tor_counts[i].map(el => { el['subintv'] = e; return el; })
            });

            all_tor_counts = tor_data.filter(el => el.length > 0).map(el => el[el.length - 1]).map(el => el['ntor'] + el['ntor_base']);
        }

        const yub = Math.max(Math.max(...all_tor_counts) * 1.1, 1.1);

        // Create D3 scales
        var t_scale = d3.scaleUtc()
            .domain([t_bnds_intv.start.toJSDate(),
                     t_bnds_intv.end.toJSDate()])
            .range([this.props['x'], this.props['x'] + this.props['width']]);

        var y_scale = d3.scaleLinear()
            .domain([0, yub])
            .range([height + this.props['y'], this.props['y']]);

        var root = d3.select(this.root);

        // Clear everything
        root.selectAll('*')
           .remove();
   
        const colors = this.partitioner.colors;
        const part_keys = this.partitioner.part_keys;

        // Plot data
        root.append('g')
            .selectAll('g')
            .data(tor_data)
            .enter()
                .selectAll('rect')
                .data(d => d)
                .enter()
                    .append('rect')
                    .attr('x', d => t_scale(d['subintv'].start.toJSDate()))
                    .attr('y', d => y_scale(d['ntor'] + d['ntor_base']))
                    .attr('width', d => t_scale(d['subintv'].end.toJSDate()) - t_scale(d['subintv'].start.toJSDate()) )
                    .attr('height', d => (height - (y_scale(d['ntor']) - this.props['y'])))
                    .attr('fill', d => colors[part_keys.indexOf(d['pk'])])
                    .attr('clip-path', 'url(#axis-face)');

        // Add t axis
        root.append('g')
            .attr('transform', 'translate(0, ' + (this.props['y'] + height) + ')')
            .call(d3.axisTop(t_scale)
                    .tickSizeOuter(0)
                    .tickSizeInner(height)
                    .ticks(d3_ticks)
                    .tickFormat(time_format))
            .call(g => g.selectAll('.tick line')
                        .attr('stroke-dasharray', '2,2'))
            .call(g => g.selectAll(".tick text")
                        .attr("y", 9)
                        .attr("dx", 0))
            .selectAll(".tick text")
            .call(wrap);

        const mag = Math.floor(Math.log10(yub));
        const mag_ten = Math.pow(10, mag);
        const mag_m1_ten = Math.pow(10, mag - 1);
        let dyt;
        if (yub < 1.2 * mag_ten) {
            dyt = mag_m1_ten;
        }
        else if (yub < 3 * mag_ten) {
            dyt = 2 * mag_m1_ten;
        }
        else if (yub < 6 * mag_ten) {
            dyt = 5 * mag_m1_ten;
        }
        else {
            dyt = mag_ten;
        }

        dyt = Math.max(dyt, 1);

        let ticks = [];
        for (let itick = 0; itick < yub; itick += dyt) {
            ticks.push(itick);
        }

        // Add y axis
        root.append('g')
            .attr('transform', 'translate(' + this.props['x'] + ', 0)')
            .call(d3.axisRight(y_scale)
                    .tickValues(ticks)
                    .tickFormat(x => `${x.toFixed(0)}`)
                    .tickSizeOuter(0)
                    .tickSizeInner(this.props['width']))
            .call(g => g.selectAll('.tick line')
                        .attr('stroke-dasharray', '2,2'))
            .call(g => g.selectAll(".tick text")
                        .attr("y", 0)
                        .attr("dx", -this.props['width'] - 4))
                        .attr('text-anchor', 'end');

        const label_offset = (mag + 1) * 5 + 7;
        // Add y axis label
        root.append('text')
            .attr('text-anchor', 'middle')
            .attr('font-size', '15')
            .attr('transform', 'translate(' + (this.props['x'] - label_offset) + ', ' + (this.props['y'] + this.props['height'] / 2) + ') rotate(270)')
            .text('# of Tornadoes')
        
        if (draw_orient) {
            // Draw orientation label (the one in the bottom left corner)
            var tl_labels = root.selectAll('.tick text tspan:nth-child(2)');
            var orient_label_str = tl_format(t_bnds_intv.start.toJSDate());
            if (tl_labels.size() > 0) {
                var first_label = tl_labels.filter((d, i) => i === 0)
                var first_label_rect = first_label.node().getBoundingClientRect();
                var orient_label_x_cap = first_label_rect.left - this.props['x'];

                if (first_label_rect.left < this.props['x']){
                    first_label.attr('dx', this.props['x'] - first_label_rect.left);
                }
            }
            else {
                var orient_label_x_cap = this.props['width'];
            }
    
            root.append('g')
                .attr('transform', 'translate(' + this.props['x'] + ', ' + (height + this.props['y']) + ')')
                .attr('fill', 'none')
                .attr('font-size', '10')
                .append('text')
                .attr('class', 'orient-label')
                .attr('fill', 'currentColor')
                .attr('x', 0)
                .attr('y', 9.2)
                .attr('dy', '1.1em')
                .attr('font-size', '1.5em')
                .text(orient_label_str);
            
            var orient_label_width = root.select('.orient-label').node().getBoundingClientRect().width;
            root.select('.orient-label')
               .attr('dx', Math.min(0, orient_label_x_cap - orient_label_width - 4))
        }

        // Add bounding rectangle
        root.append('g')
            .append('rect')
            .attr('x', this.props['x'])
            .attr('y', this.props['y'])
            .attr('width', this.props['width'])
            .attr('height', this.props['height'])
            .attr('stroke', '#000000')
            .attr('fill', 'none');

        root.selectAll('text')
            .attr('font-family', font_name);
    }

    render() {
        return (
            <g className='timeline-plot' ref={el => this.root = el}></g>
        );
    }
}

export default TimelinePlot;